// API client: JSON envelopes, bearer token, one automatic refresh on 401.
import { getLang, t } from './i18n.js';

const KEY = 'verispace.session';
const mem = { session: null };
try { mem.session = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { /* storage unavailable */ }

const listeners = new Set();
export const onSessionChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const getSession = () => mem.session;
export function setSession(s) {
  mem.session = s;
  try { s ? localStorage.setItem(KEY, JSON.stringify(s)) : localStorage.removeItem(KEY); } catch { /* ignore */ }
  listeners.forEach((fn) => fn(s));
}

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.error?.message || 'Request failed');
    this.status = status;
    this.code = body?.error?.code || 'network_error';
    this.details = body?.error?.details || [];
  }
}

const MESSAGES = {
  validation_error: t('ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบช่องที่ระบุ', 'Some details are not valid. Please check the marked fields'),
  otp_invalid: t('รหัส OTP ไม่ถูกต้องหรือหมดอายุ กรุณาขอรหัสใหม่', 'The OTP is incorrect or has expired. Please request a new code'),
  invalid_credentials: t('เบอร์โทรหรือรหัสผ่านไม่ถูกต้อง', 'Incorrect phone number or password'),
  unauthorized: t('กรุณาเข้าสู่ระบบอีกครั้ง', 'Please sign in again'),
  account_suspended: t('บัญชีนี้ถูกระงับ กรุณาติดต่อทีมงาน', 'This account is suspended. Please contact our team'),
  forbidden: t('บัญชีของคุณไม่มีสิทธิ์ทำรายการนี้', 'Your account is not allowed to do this'),
  verification_required: t('ต้องยืนยันตัวตนให้ผ่านก่อนจึงจะเผยแพร่ประกาศได้', 'Identity verification must be approved before you can publish a listing'),
  plan_limit_reached: t('จำนวนประกาศออนไลน์ถึงขีดจำกัดของแพ็กเกจแล้ว', 'You have reached the online listing limit of your plan'),
  not_found: t('ไม่พบข้อมูลที่ต้องการ', 'We could not find what you were looking for'),
  conflict: t('ทำรายการไม่ได้เพราะข้อมูลซ้ำหรือถึงขีดจำกัด', 'This could not be done because of a duplicate or a limit'),
  invalid_state_transition: t('ทำรายการนี้ไม่ได้ในสถานะปัจจุบัน', 'This cannot be done in the current status'),
  payload_too_large: t('ไฟล์ใหญ่เกิน 10 MB', 'The file is larger than 10 MB'),
  unsupported_media_type: t('รองรับเฉพาะไฟล์ JPEG, PNG และ WebP', 'Only JPEG, PNG and WebP files are supported'),
  payment_failed: t('ระบบชำระเงินยังไม่เปิดให้บริการ หรือชำระเงินไม่สำเร็จ', 'Payments are not available yet, or the payment failed'),
  insufficient_credits: t('เครดิตไม่พอ กรุณาเติมเครดิต', 'Not enough credits. Please top up'),
  rate_limited: t('ทำรายการถี่เกินไป กรุณารอสักครู่แล้วลองใหม่', 'Too many requests. Please wait a moment and try again'),
  internal_error: t('ระบบขัดข้อง กรุณาลองใหม่อีกครั้ง', 'Something went wrong. Please try again'),
  no_backend: t('ยังไม่ได้เชื่อมต่อเซิร์ฟเวอร์ของ OneProp เปิดเว็บด้วย start-demo หรือเปิด backend ก่อน แล้วลองใหม่', 'The OneProp server is not connected. Open the site with start-demo, or start the backend first, then try again'),
  network_error: t('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาตรวจสอบอินเทอร์เน็ต', 'Could not reach the server. Please check your internet connection'),
};
export const errorText = (e) => MESSAGES[e?.code] || MESSAGES.internal_error;
// Maps server field errors to { field: true } so forms can mark inputs.
export const fieldErrors = (e) => Object.fromEntries((e?.details || []).map((d) => [d.field, d.message]));

let refreshing = null;
async function refresh() {
  if (!mem.session?.refresh_token) return false;
  refreshing ||= (async () => {
    try {
      const res = await fetch('/v1/auth/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: mem.session.refresh_token }) });
      if (!res.ok) { setSession(null); return false; }
      const { data } = await res.json();
      setSession({ ...mem.session, ...data });
      return true;
    } catch { return false; } finally { refreshing = null; }
  })();
  return refreshing;
}

export async function api(method, path, { body, form, query, idem } = {}, retried = false) {
  const headers = {};
  if (mem.session?.access_token) headers.Authorization = `Bearer ${mem.session.access_token}`;
  if (idem) headers['Idempotency-Key'] = idem;
  headers['X-Lang'] = getLang(); // listings come back in the language the viewer picked (backend core/translate.py)
  let payload;
  if (form) payload = form;
  else if (body !== undefined) { headers['Content-Type'] = 'application/json'; payload = JSON.stringify(body); }
  let url = path;
  if (query) {
    const qs = new URLSearchParams(Object.entries(query).filter(([, v]) => v !== '' && v !== null && v !== undefined && v !== false));
    if ([...qs].length) url += `?${qs}`;
  }
  let res;
  try { res = await fetch(url, { method, headers, body: payload }); } catch { throw new ApiError(0, null); }
  if (res.status === 401 && !retried && mem.session && !path.startsWith('/v1/auth/')) {
    if (await refresh()) return api(method, path, { body, form, query, idem }, true);
  }
  const text = await res.text();
  let json = null;
  // A page of HTML instead of JSON means there is no API behind this address (for example a static file server).
  try { json = text ? JSON.parse(text) : null; } catch { throw new ApiError(res.status, { error: { code: 'no_backend' } }); }
  if (!res.ok) throw new ApiError(res.status, json);
  return json;
}
export const get = (path, query) => api('GET', path, { query });
export const post = (path, body, opts = {}) => api('POST', path, { body, ...opts });
export const patch = (path, body) => api('PATCH', path, { body });
export const put = (path, body) => api('PUT', path, { body });
export const del = (path) => api('DELETE', path);
export const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());
